import { z } from 'zod'

export const modelModeSchema = z.enum([
  'chat',
  'completion',
  'embedding',
  'audio_transcription',
  'audio_speech',
  'image_generation',
  'rerank',
  'moderation',
  'unknown',
])

export type ModelMode = z.infer<typeof modelModeSchema>

export const modelCapabilitiesSchema = z.object({
  vision: z.boolean(),
  functionCalling: z.boolean(),
  reasoning: z.boolean(),
  promptCaching: z.boolean(),
  streaming: z.boolean(),
  audioInput: z.boolean(),
  audioOutput: z.boolean(),
  pdfInput: z.boolean(),
})

export type ModelCapabilities = z.infer<typeof modelCapabilitiesSchema>

/**
 * Optional display metadata. LiteLLM omits these for some models, and a
 * missing key is not the same as null to a strict schema — without this, one
 * model without a token limit fails validation and takes the whole catalogue
 * page down with it. Identity and capabilities stay strict, because a gap
 * there really is a contract break.
 */
const optionalNumber = z
  .number()
  .nullish()
  .transform((value) => value ?? null)

/** A flattened, UI-shaped view of LiteLLM's `/model/info` payload. */
export const modelSchema = z.object({
  id: z.string(),
  name: z.string(),
  provider: z.string(),
  mode: modelModeSchema,
  maxInputTokens: optionalNumber,
  maxOutputTokens: optionalNumber,
  inputCostPerToken: optionalNumber,
  outputCostPerToken: optionalNumber,
  inputCostPerSecond: optionalNumber,
  capabilities: modelCapabilitiesSchema,
})

export type Model = z.infer<typeof modelSchema>

export const modelListSchema = z.object({
  data: z.array(modelSchema),
  /**
   * The model a client should preselect when the user has never chosen one.
   *
   * Always the name of a model present in `data`, or null when the catalogue is
   * empty — so a client can use it without checking, and an admin naming a
   * model that later disappears from LiteLLM cannot leave a dangling default.
   */
  defaultModel: z.string().nullable(),
})

export type ModelList = z.infer<typeof modelListSchema>
